import * as React from 'react';

import CDPReviewsSchemaMarkup from 'bundles/reviews/components/seo/CDPReviewsSchemaMarkup';
import type { ReviewsPageCourseInfo, ReviewsPageSchemaReview } from 'bundles/reviews/hooks/useReviewsPageData';
import type { ReviewRouter } from 'bundles/reviews/types/reviewTypes';
import CourseraMetatags from 'bundles/seo/components/CourseraMetatags';

type Props = {
  courseInfo: ReviewsPageCourseInfo | null;
  router: ReviewRouter;
  schemaReviews: Array<ReviewsPageSchemaReview>;
};

const sliceFirstNChars = (str: string, n: number, addEllipses = true) => {
  if (!str) {
    return null;
  }

  if (str.length <= n) {
    return str;
  }

  const slicedStr = str.slice(0, n);

  return addEllipses ? `${slicedStr}...` : slicedStr;
};

const ReviewsPageSeoElements = ({ courseInfo, router, schemaReviews }: Props) => {
  const cdpMetadata = courseInfo?.xdpMetadata?.cdpMetadata;

  if (!courseInfo || !cdpMetadata) {
    return null;
  }

  const courseName = courseInfo.name;
  const { location } = router;
  const { partners, ratings, topReviews } = cdpMetadata;
  const firstPartner = partners?.[0];
  const topReviewComment = topReviews?.[0]?.comment;
  const first100CharsFromTopReview = topReviewComment != null ? sliceFirstNChars(topReviewComment, 100) : null;
  const desc = `Find helpful learner reviews, feedback, and ratings for ${courseName} from ${
    firstPartner?.name || 'Coursera'
  }. Read stories and highlights from Coursera learners who completed ${courseName} and wanted to share their experience.`;
  const description = first100CharsFromTopReview ? `${desc} ${first100CharsFromTopReview}` : desc;
  const ratingScore = ratings?.averageFiveStarRating != null ? ratings.averageFiveStarRating.toFixed(1) : undefined;

  const schemaMarkupData = {
    aggregrateRating: {
      ratingScore,
      ratingCount: ratings?.ratingCount != null ? String(ratings.ratingCount) : undefined,
      reviewCount: ratings?.commentCount != null ? String(ratings.commentCount) : undefined,
    },
    slug: courseInfo.slug,
    courseName,
    courseDescription: cdpMetadata.description,
    partnerName: firstPartner?.name,
    partnerLogoUrl: firstPartner?.logo || firstPartner?.squareLogo,
    reviews: schemaReviews,
  };

  return (
    <>
      <CourseraMetatags
        metaNameAndProperties={{
          title: `Learner Reviews & Feedback for ${courseName} Course`,
          description,
          absolutePageHref: `https://${location.hostname}${location.pathname}${location.search ?? ''}`,
          disableDescLimit: true,
          disableCourseraDescriptionSuffix: true,
          disableCrawlerIndexing: true,
        }}
        hrefLangOverrides={() => []}
      />
      <CDPReviewsSchemaMarkup CDPReviewsMetaData={schemaMarkupData} />
    </>
  );
};

export default ReviewsPageSeoElements;
